import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, getToken, qs } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Champ, Modale, Squelette } from '../components/ui';
import { urlImpression, urlApi } from '../api/config';
import { Signature } from '../components/SchemaVoiture';
import { centimes } from '../lib/format';
import { dateFr, dateHeureFr, montant, nomClient, CARBURANT_LABEL } from '../lib/format';
import type { Client, Contrat, Reservation as Resa, Vehicule } from '../api/types';

/** Ce qui touche a l'argent doit etre tranche par Hichem, pas par Reda. */
const EST_FINANCIER = /prix|tarif|montant|solde|caution|supplément|supplement|remise|acompte|facture|total|devise/i;

export default function Reservation() {
  const { id } = useParams();
  const nav = useNavigate();
  const { peut, estAgent } = useAuth();
  const [r, setR] = useState<Resa | null>(null);
  const [contrat, setContrat] = useState<Contrat | null>(null);
  const [caution, setCaution] = useState<{ _id: string; montantMinor: number; devise: string; statut: string; dechargeRemiseNumero: string; dechargeRestitutionNumero?: string } | null>(null);
  const [demande, setDemande] = useState(false);
  const [valeurActuelle, setValeurActuelle] = useState('');
  const [annulation, setAnnulation] = useState(false);
  const [resiliation, setResiliation] = useState(false);
  const [motifAction, setMotifAction] = useState('');
  const [saisirCaution, setSaisirCaution] = useState(false);
  const [envoiAction, setEnvoiAction] = useState(false);
  const [factures, setFactures] = useState<{ _id: string; numero: string; type: string; date: string; totalMinor: number }[]>([]);
  const [valeurDemandee, setValeurDemandee] = useState('');
  const [caut, setCaut] = useState(false);
  const [cf, setCf] = useState({ montant: '', devise: 'DZD', forme: 'especes', piece: '', sigC: '', sigA: '' });
  const [champ, setChamp] = useState('');
  const [motif, setMotif] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!id) return;
    api.get<Resa>(`/reservations/${id}`).then(async (res) => {
      setR(res);
      const [c] = await api.get<Contrat[]>(`/contrats${qs({ reservation: res._id })}`);
      if (c) {
        const d = await api.get<{ contrat: Contrat; caution: typeof caution }>(`/contrats/${c._id}`);
        setContrat(d.contrat); setCaution(d.caution);
        api.get<typeof factures>(`/factures${qs({ contract: c._id })}`).then(setFactures).catch(() => {});
      }
    });
  }, [id]);

  if (!r) return <Squelette lignes={6} />;
  const v = r.vehicle as Vehicule;
  const c = r.customer as Client;

  async function ouvrir(sens: 'depart' | 'retour') {
    const ct = contrat ?? (await api.post<Contrat>('/contrats', { reservation: r!._id }));
    nav(`/contrats/${ct._id}/etat-des-lieux/${sens}`);
  }

  /**
   * L'ordre reel du comptoir : on edite le contrat, on le fait signer, on
   * encaisse la caution, et seulement ensuite on descend faire l'etat des
   * lieux. Tant que ce bouton n'existait pas, le seul moyen d'obtenir un
   * contrat imprimable etait de commencer l'etat des lieux — une saisie
   * irreversible — puis de revenir en arriere.
   */
  async function genererContrat() {
    if (contrat) return;
    setContrat(await api.post<Contrat>('/contrats', { reservation: r!._id }));
    setMsg('Contrat genere — vous pouvez l\'imprimer et enregistrer la caution.');
  }

  async function creerCaution() {
    if (!contrat) return;
    const d = await api.post<NonNullable<typeof caution>>('/cautions', {
      contract: contrat._id, montantMinor: centimes(Number(cf.montant)),
      devise: cf.devise, forme: cf.forme, numeroPiece: cf.piece || undefined,
      signatureClient: cf.sigC, signatureAgent: cf.sigA,
    });
    setCaution(d); setCaut(false);
    setMsg(`Caution enregistree — decharge ${d.dechargeRemiseNumero} prete a imprimer.`);
    window.open(urlImpression(`/cautions/${d._id}/imprimer/remise`, getToken()), '_blank');
  }

  /**
   * Une demande de correction sans les deux valeurs n'est pas exploitable : le
   * gerant recoit « champ : km au depart » et un motif en texte libre, sans
   * savoir quoi mettre. Et le journal enregistrait `avant: {}` / `apres: {}`
   * alors que l'ecran promet que « les deux valeurs restent au journal ».
   */
  /**
   * Annulation et resiliation existaient cote serveur — avec leurs droits, leur
   * motif obligatoire et leur trace au journal — mais aucun bouton nulle part.
   * L'article 07 prevoit la perte de la caution en cas de manquement : c'est la
   * case a cocher de la resiliation.
   */
  async function annuler() {
    setEnvoiAction(true);
    try {
      await api.post(`/reservations/${r!._id}/annuler`, { motif: motifAction.trim() });
      setAnnulation(false); setMotifAction('');
      setMsg('Location annulée — le motif est au journal.');
      setR(await api.get<Resa>(`/reservations/${r!._id}`));
    } catch (e) { setMsg(e instanceof ApiError ? e.erreur : "Annulation impossible"); }
    finally { setEnvoiAction(false); }
  }

  async function resilier() {
    if (!contrat) return;
    setEnvoiAction(true);
    try {
      await api.post(`/contrats/${contrat._id}/resilier`, { motif: motifAction.trim(), saisirCaution });
      setResiliation(false); setMotifAction(''); setSaisirCaution(false);
      setMsg(`Contrat résilié${saisirCaution ? ' — caution saisie (article 07)' : ''}. Le motif est au journal.`);
      const [c] = await api.get<Contrat[]>(`/contrats${qs({ reservation: r!._id })}`);
      if (c) setContrat((await api.get<{ contrat: Contrat }>(`/contrats/${c._id}`)).contrat);
    } catch (e) { setMsg(e instanceof ApiError ? e.erreur : 'Résiliation impossible'); }
    finally { setEnvoiAction(false); }
  }

  async function envoyerDemande() {
    await api.post('/corrections', {
      // 'Reservation' sans accent : c'est ce que le serveur ecrit partout
      // ailleurs, et le filtre du journal cherche cette valeur exacte.
      entite: 'Reservation', entiteId: r!._id, libelleEntite: `Reservation ${r!.numero}`,
      champ, valeurActuelle, valeurDemandee, motif,
      // Une correction qui porte sur de l'argent doit reveiller le garde-fou
      // « seul Hichem tranche une donnee financiere ».
      natureFinanciere: EST_FINANCIER.test(champ),
    });
    setDemande(false); setMotif(''); setChamp('');
    setValeurActuelle(''); setValeurDemandee('');
    setMsg('Demande envoyée — Reda et Hichem ont été prévenus.');
  }

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{r.numero} <Badge statut={r.statut} /></h1>
          <p>{v.marque} {v.modele} · {v.immatriculation} — {nomClient(c)} · {dateFr(r.dateDebut)} → {dateFr(r.dateFin)}</p>
        </div>
        <div className="actions">
          {!contrat && peut('creer.contrat') && (
            <button className="btn" onClick={() => void genererContrat()}>Générer le contrat</button>
          )}
          {contrat && <a className="btn" target="_blank" rel="noreferrer" href={urlImpression(`/contrats/${contrat._id}/imprimer`, getToken())}>Imprimer le contrat</a>}
          {contrat && !caution && <button className="btn" onClick={() => setCaut(true)}>Enregistrer la caution</button>}
          {contrat && contrat.soldeMinor > 0 && peut('creer.mouvementCaisse') && (
            <Link className="btn" to={`/caisse?contrat=${contrat._id}`}>Encaisser le solde · {montant(contrat.soldeMinor)}</Link>
          )}
          {!contrat?.remise && <button className="btn btn-p" onClick={() => void ouvrir('depart')}>Faire le départ</button>}
          {contrat?.remise && !contrat?.restitution && <button className="btn btn-p" onClick={() => void ouvrir('retour')}>Faire le retour</button>}
          {peut('annuler.reservation') && !['annulee', 'terminee'].includes(r.statut) && !contrat?.remise && (
            <button className="btn btn-d" onClick={() => { setAnnulation(true); setMotifAction(''); }}>Annuler la location</button>
          )}
          {peut('resilier.contrat') && contrat && contrat.statut === 'actif' && (
            <button className="btn btn-d" onClick={() => { setResiliation(true); setMotifAction(''); }}>Résilier le contrat</button>
          )}
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}
      {c.listeNoire && <div style={{ marginBottom: 14 }}><Bandeau type="ko" titre="Client en liste noire">{c.motifListeNoire}</Bandeau></div>}
      {(r.derogations ?? []).map((d, i) => (
        <div key={i} style={{ marginBottom: 14 }}>
          <Bandeau type="warn" titre={`Derogation article 01 — ${d.detail}`}>Motif : {d.motif} · accordée le {dateFr(d.date)}</Bandeau>
        </div>
      ))}

      {/*
        * Tout ce qui s'imprime, au meme endroit.
        *
        * Le contrat etait imprimable ici, mais la decharge de RESTITUTION — celle
        * qu'on signe en rendant la caution, et qui est l'une des trois pieces qui
        * protegent l'agence — n'etait atteignable que depuis un autre ecran. Les
        * factures du dossier n'etaient nulle part.
        */}
      {contrat && (
        <div style={{ marginBottom: 16 }}>
          <Carte titre="Documents à imprimer" pad={false}>
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <tbody>
                  <tr>
                    <td><b>Contrat de location</b><div className="mini sourdine">n° {contrat.numero} · bilingue, avec l'état des lieux</div></td>
                    <td className="droite">
                      <a className="btn btn-s" target="_blank" rel="noreferrer"
                         href={urlImpression(`/contrats/${contrat._id}/imprimer`, getToken())}>Imprimer</a>
                    </td>
                  </tr>
                  {caution && (
                    <tr>
                      <td>
                        <b>Décharge de caution — remise</b>
                        <div className="mini sourdine">n° {caution.dechargeRemiseNumero} · {montant(caution.montantMinor)} reçus</div>
                      </td>
                      <td className="droite">
                        <a className="btn btn-s" target="_blank" rel="noreferrer"
                           href={urlImpression(`/cautions/${caution._id}/imprimer/remise`, getToken())}>Imprimer</a>
                      </td>
                    </tr>
                  )}
                  {caution?.dechargeRestitutionNumero && (
                    <tr>
                      <td>
                        <b>Décharge de caution — restitution</b>
                        <div className="mini sourdine">n° {caution.dechargeRestitutionNumero} · <Badge statut={caution.statut} /></div>
                      </td>
                      <td className="droite">
                        <a className="btn btn-s" target="_blank" rel="noreferrer"
                           href={urlImpression(`/cautions/${caution._id}/imprimer/restitution`, getToken())}>Imprimer</a>
                      </td>
                    </tr>
                  )}
                  {factures.map((fa) => (
                    <tr key={fa._id}>
                      <td>
                        <b>Facture {fa.type === 'avoir' ? "d'avoir" : fa.type === 'acompte' ? "d'acompte" : 'de solde'}</b>
                        <div className="mini sourdine">n° {fa.numero} · {dateFr(fa.date)} · {montant(fa.totalMinor)}</div>
                      </td>
                      <td className="droite">
                        <a className="btn btn-s" target="_blank" rel="noreferrer"
                           href={urlImpression(`/factures/${fa._id}/imprimer`, getToken())}>Imprimer</a>
                      </td>
                    </tr>
                  ))}
                  {!caution && factures.length === 0 && (
                    <tr>
                      <td colSpan={2} className="mini sourdine">
                        La décharge apparaîtra ici dès que la caution sera enregistrée, et les factures
                        dès qu'elles seront émises.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Carte>
        </div>
      )}

      <div className="grille g2" style={{ alignItems: 'start' }}>
        <Carte titre="Location">
          <table className="tbl compact">
            <tbody>
              <tr><td>Tarif de référence</td><td className="droite montant">{montant(r.tarifReferenceMinor)} / jour</td></tr>
              <tr><td>Prix negocie</td><td className="droite montant">{montant(r.tarifNegocieMinor)} / jour
                {r.tarifNegocieMinor !== r.tarifReferenceMinor && (
                  <div className="mini sourdine" style={{ fontWeight: 400 }}>
                    {Math.round(((r.tarifNegocieMinor - r.tarifReferenceMinor) / r.tarifReferenceMinor) * 1000) / 10} %
                  </div>)}
              </td></tr>
              <tr><td>{r.joursFactures} jour(s) · saison {r.saison}</td><td className="droite montant">{montant(r.totalLocationMinor)}</td></tr>
              <tr><td>Kilométrage</td><td className="droite">{r.kmIllimite ? 'illimite' : `${r.forfaitKmJour} km / jour`}</td></tr>
              <tr><td><b>Total prévu</b></td><td className="droite montant"><b>{montant(r.totalPrevuMinor)}</b></td></tr>
            </tbody>
          </table>
        </Carte>

        <Carte titre="Contrat et caution">
          {!contrat ? (
            <p className="sourdine mini">Aucun contrat encore généré. Il sera créé au moment du départ.</p>
          ) : (
            <table className="tbl compact">
              <tbody>
                <tr><td>Numéro</td><td className="droite"><b>{contrat.numero}</b> <Badge statut={contrat.statut} /></td></tr>
                <tr><td>État des lieux départ</td><td className="droite">{contrat.remise ? dateHeureFr(contrat.remise.dateHeure) : '—'}</td></tr>
                <tr><td>État des lieux retour</td><td className="droite">{contrat.restitution ? dateHeureFr(contrat.restitution.dateHeure) : '—'}</td></tr>
                <tr><td>Encaissé</td><td className="droite montant">{montant(contrat.totalEncaisseMinor)}</td></tr>
                <tr><td><b>Solde dû</b></td><td className="droite montant"><b>{montant(contrat.soldeMinor)}</b></td></tr>
                {caution && (
                  <tr><td>Caution</td><td className="droite montant">{montant(caution.montantMinor, caution.devise)}
                    <div className="mini sourdine" style={{ fontWeight: 400 }}>{caution.dechargeRemiseNumero} · <Badge statut={caution.statut} /></div>
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </Carte>
      </div>

      {contrat?.supplementsRetour?.length ? (
        <div style={{ marginTop: 16 }}>
          <Carte titre="Suppléments facturés au retour" pad={false}>
            <table className="tbl">
              <thead><tr><th>Supplément</th><th>Calcul</th><th className="droite">Montant</th></tr></thead>
              <tbody>
                {contrat.supplementsRetour.map((l) => (
                  <tr key={l.code}>
                    <td><b>{l.libelle}</b> {l.modifieManuellement && <Badge statut="retour" libelle="modifié manuellement" />}</td>
                    <td className="mini sourdine">{l.formule}</td>
                    <td className="droite montant">{montant(l.totalMinor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Carte>
        </div>
      ) : null}

      {contrat?.remise && (
        <div style={{ marginTop: 16 }}>
          <Carte titre="État des lieux">
            <div className="grille g2">
              {(['remise', 'restitution'] as const).map((k) => {
                const e = contrat[k];
                return (
                  <div key={k}>
                    <div className="micro">{k === 'remise' ? 'Départ' : 'Retour'}</div>
                    {!e ? <p className="sourdine mini">Pas encore saisi.</p> : (
                      <table className="tbl compact">
                        <tbody>
                          <tr><td>Date</td><td className="droite">{dateHeureFr(e.dateHeure)}</td></tr>
                          <tr><td>Kilométrage</td><td className="droite num">{e.km.toLocaleString('fr-FR')} km</td></tr>
                          <tr><td>Carburant</td><td className="droite">{CARBURANT_LABEL[e.carburantQuarts]}</td></tr>
                          <tr><td>Propreté</td><td className="droite">{e.propreteInterieur}/10 int. · {e.propreteExterieur}/10 ext.</td></tr>
                          <tr><td>Dommages</td><td className="droite">{e.dommages.length}{e.dommages.some((d) => d.nouveau) ? ` (dont ${e.dommages.filter((d) => d.nouveau).length} nouveau(x))` : ''}</td></tr>
                          <tr><td>Photos</td><td className="droite">{e.photos.length} / 6</td></tr>
                        </tbody>
                      </table>
                    )}
                    {e && e.photos.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginTop: 8 }}>
                        {e.photos.map((p, i) => (
                          <a key={i} href={urlApi(p)} target="_blank" rel="noreferrer"
                             style={{ aspectRatio: '4/3', borderRadius: 6, background: `center/cover no-repeat url(${urlApi(p)})`, border: '1px solid var(--bord)' }}
                             title="Ouvrir en grand" />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Carte>
        </div>
      )}

      {estAgent && (
        <div style={{ marginTop: 16 }}>
          <Bandeau type="info" titre="Votre saisie est définitive">
            Vous ne pouvez pas modifier ce dossier. Si quelque chose est faux, demandez une correction :
            Reda ou Hichem la traite, et les deux valeurs restent au journal.
            <div style={{ marginTop: 10 }}><button className="btn btn-s" onClick={() => setDemande(true)}>Demander une correction</button></div>
          </Bandeau>
        </div>
      )}
      {!estAgent && peut('annuler.reservation') && r.statut !== 'annulee' && (
        <div style={{ marginTop: 16 }}>
          <Link className="btn" to="/planning">Voir dans le planning</Link>
        </div>
      )}

      {caut && (
        <Modale titre="Enregistrer la caution" onFermer={() => setCaut(false)}>
          <p className="mini sourdine" style={{ marginBottom: 12 }}>
            La caution n'est pas un montant fixe : vous la décidez pour cette location. Une décharge numérotée
            et signée sera générée immediatement.
          </p>
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="grille g2">
              <Champ label="Montant"><input type="number" value={cf.montant} onChange={(e) => setCf({ ...cf, montant: e.target.value })} autoFocus /></Champ>
              <Champ label="Devise">
                <select value={cf.devise} onChange={(e) => setCf({ ...cf, devise: e.target.value })}>
                  {['DZD', 'EUR', 'USD', 'TND'].map((d) => <option key={d}>{d}</option>)}
                </select>
              </Champ>
            </div>
            <div className="grille g2">
              <Champ label="Forme">
                <select value={cf.forme} onChange={(e) => setCf({ ...cf, forme: e.target.value })}>
                  <option value="especes">Espèces</option><option value="cheque">Chèque</option>
                  <option value="traite">Traite</option><option value="carte">Carte bancaire</option>
                </select>
              </Champ>
              <Champ label="N° de pièce" aide="Numéro du chèque ou de la traite.">
                <input value={cf.piece} onChange={(e) => setCf({ ...cf, piece: e.target.value })} />
              </Champ>
            </div>
            <Signature label="Signature du client" valeur={cf.sigC} onChange={(v) => setCf({ ...cf, sigC: v })} />
            <Signature label="Signature du directeur" valeur={cf.sigA} onChange={(v) => setCf({ ...cf, sigA: v })} />
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setCaut(false)}>Annuler</button>
            <button className="btn btn-p" disabled={!Number(cf.montant)} onClick={() => void creerCaution()}>
              Enregistrer et éditer la décharge
            </button>
          </div>
        </Modale>
      )}

      {annulation && (
        <Modale titre="Annuler cette location" onFermer={() => setAnnulation(false)}>
          <Bandeau type="warn" titre="Le motif est obligatoire et définitif">
            Il restera au journal avec votre nom. La location ne pourra pas être rétablie.
          </Bandeau>
          <div style={{ marginTop: 12 }}>
            <Champ label="Motif de l'annulation">
              <textarea value={motifAction} onChange={(e) => setMotifAction(e.target.value)}
                        placeholder="Client s'est désisté, voiture immobilisée…" />
            </Champ>
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setAnnulation(false)}>Revenir</button>
            <button className="btn btn-d" disabled={motifAction.trim().length < 3 || envoiAction}
                    onClick={() => void annuler()}>Annuler la location</button>
          </div>
        </Modale>
      )}

      {resiliation && (
        <Modale titre="Résilier le contrat" onFermer={() => setResiliation(false)}>
          <Bandeau type="ko" titre="Résiliation pour manquement">
            Votre article 07 prévoit la perte de la caution en cas d'usage interdit — transport de
            voyageurs, conduite par un tiers, marchandises. Le motif restera au journal.
          </Bandeau>
          <div style={{ marginTop: 12 }}>
            <Champ label="Motif de la résiliation">
              <textarea value={motifAction} onChange={(e) => setMotifAction(e.target.value)}
                        placeholder="Véhicule utilisé pour du transport de voyageurs (article 07)" />
            </Champ>
          </div>
          <label style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 12 }}>
            <input type="checkbox" checked={saisirCaution} onChange={(e) => setSaisirCaution(e.target.checked)} />
            Saisir la caution au titre de l'article 07
          </label>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setResiliation(false)}>Revenir</button>
            <button className="btn btn-d" disabled={motifAction.trim().length < 3 || envoiAction}
                    onClick={() => void resilier()}>Résilier</button>
          </div>
        </Modale>
      )}

      {demande && (
        <Modale titre="Demander une correction" onFermer={() => setDemande(false)}>
          <Champ label="Que faut-il corriger ?"><input value={champ} onChange={(e) => setChamp(e.target.value)} placeholder="km au départ, date de retour…" /></Champ>
          <div className="grille g2" style={{ marginTop: 12 }}>
            <Champ label="Valeur enregistrée" aide="Ce qui est écrit aujourd'hui.">
              <input value={valeurActuelle} onChange={(e) => setValeurActuelle(e.target.value)} placeholder="68 400" />
            </Champ>
            <Champ label="Valeur correcte" aide="Ce qu'il faut mettre à la place.">
              <input value={valeurDemandee} onChange={(e) => setValeurDemandee(e.target.value)} placeholder="64 800" />
            </Champ>
          </div>
          <div style={{ marginTop: 12 }}>
            <Champ label="Pourquoi ?"><textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Inversion de chiffres à la saisie" /></Champ>
          </div>
          {champ.length >= 2 && EST_FINANCIER.test(champ) && (
            <div style={{ marginTop: 12 }}>
              <Bandeau type="warn" titre="Correction financière">
                Elle touche à l'argent : seul Hichem pourra la trancher.
              </Bandeau>
            </div>
          )}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setDemande(false)}>Annuler</button>
            <button className="btn btn-p"
                    disabled={champ.length < 2 || motif.length < 3 || valeurDemandee.trim().length === 0}
                    onClick={() => void envoyerDemande()}>Envoyer la demande</button>
          </div>
        </Modale>
      )}
    </>
  );
}
